import React, { useState, useEffect } from 'react';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  TextField,
  IconButton,
  Chip,
  Box,
  Paper,
  TableContainer,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  CircularProgress,
} from '@mui/material';

import {
  Visibility as VisibilityIcon,
  Settings as SettingsIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  HeadsetMic as HeadsetMicIcon,
  EditNoteSharp as EditIcon,
  Pending as PendingIcon,
} from '@mui/icons-material';
import { makeStyles } from '@mui/styles';
import { useSelector, useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { api } from 'common';
import moment from 'moment';
import AlertDialog from '../components/AlertDialog';
import PermissionGuard from '../components/PermissionGuard';
import { colors } from '../components/Theme/WebTheme';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
  },
  content: {
    flexGrow: 1,
    padding: '15px',
    marginTop: '15px', // 64px + 15px
    marginLeft: '15px', // Sidebar width
  },
  pageTitle: {
    fontSize: '2rem',
    fontWeight: 'bold',
    color: '#333',
    marginBottom: theme.spacing(3),
  },
  statsCard: {
    textAlign: 'center',
    padding: theme.spacing(2),
    borderRadius: '12px',
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    marginBottom: theme.spacing(2),
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  },
  statsNumber: {
    fontSize: '2.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
  },
  statsLabel: {
    fontSize: '0.9rem',
    opacity: 0.9,
  },
  filterCard: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
  },
  tableCard: {
    borderRadius: '12px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.1)',
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: '#f8f9fa',
    '& .MuiTableCell-head': {
      fontWeight: 'bold',
      fontSize: '0.95rem',
      borderBottom: '2px solid #dee2e6',
    }
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(8),
    color: '#6c757d',
  },
  emptyIcon: {
    fontSize: '4rem',
    marginBottom: theme.spacing(2),
    opacity: 0.3,
  },
  emptyTitle: {
    fontSize: '1.5rem',
    fontWeight: 'bold',
    marginBottom: theme.spacing(1),
    color: '#495057',
  },
  emptyDescription: {
    fontSize: '0.8rem',
    minWidth: "400px",
    maxWidth: '50%',
    margin: '0 auto',
    lineHeight: 1.6,
  },
  manageButton: {
    borderRadius: '25px',
    padding: '10px 24px',
    textTransform: 'none',
    fontWeight: 'bold',
    boxShadow: '0 4px 15px rgba(0,0,0,0.2)',
  },
  statusChip: {
    fontWeight: 'bold',
    textTransform: 'uppercase',
    fontSize: '0.75rem',
  },
  actionButton: {
    borderRadius: '50%',
    width: '40px',
    height: '40px',
  },
  categoryCard: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: theme.spacing(2),
    marginBottom: theme.spacing(1),
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    '&:hover': {
      backgroundColor: '#f8f9fa',
    }
  },
}));

export default function SupportManagement() {
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  
  const {
    fetchSupportRequests,
    editSupportRequest,
    fetchSupportCategories,
    editSupportCategory,
    fetchComplain,
    editComplain
  } = api;

  const supportRequestData = useSelector(state => state.supportrequestdata);
  const supportCategoryData = useSelector(state => state.supportcategorydata);
  const complainData = useSelector(state => state.complaindata);
  const auth = useSelector(state => state.auth);
  const settings = useSelector(state => state.settingsdata.settings);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [editingRowId, setEditingRowId] = useState(null);
  const [editingData, setEditingData] = useState({});
  
  // Modals state
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [categoriesModalOpen, setCategoriesModalOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Category management state
  const [newCategory, setNewCategory] = useState({ name: '', description: '' });
  
  // Request editing state
  const [editingStatus, setEditingStatus] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  
  // Alert state
  const [commonAlert, setCommonAlert] = useState({ open: false, msg: '' });
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    if (fetchSupportRequests && fetchSupportCategories && fetchComplain) {
      console.log('🔍 Fetching support data...');
      dispatch(fetchSupportRequests());
      dispatch(fetchSupportCategories());
      dispatch(fetchComplain());
    } else {
      console.log('❌ Support actions not available:', { fetchSupportRequests, fetchSupportCategories, fetchComplain });
    }
  }, [dispatch, fetchSupportRequests, fetchSupportCategories, fetchComplain]);

  // Debug logging
  useEffect(() => {
    console.log('📊 Support Request Data:', supportRequestData);
    console.log('📂 Support Category Data:', supportCategoryData);
    console.log('📋 Complain Data:', complainData);
  }, [supportRequestData, supportCategoryData, complainData]);



  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleViewRequest = (request) => {
    setSelectedRequest(request);
    setEditingStatus(request.status);
    setAdminNotes(request.adminNotes || '');
    setViewModalOpen(true);
  };

  const handleEditComplaint = (complaint) => {
    if (editingRowId === complaint.id) {
      // Save changes and exit edit mode
      const updatedComplaint = {
        ...complaint,
        ...editingData[complaint.id],
        processDate: new Date().getTime()
      };
      
      // Dispatch update action
      if (editComplain) {
        dispatch(editComplain(updatedComplaint, "Update"));
      }
      
      setEditingRowId(null);
      setEditingData({});
    } else {
      // Enter edit mode for this row
      setEditingRowId(complaint.id);
      setEditingData({
        ...editingData,
        [complaint.id]: { check: complaint.check }
      });
    }
  };

  const handleStatusUpdate = async (status) => {
    if (selectedRequest && !updatingStatus) {
      setUpdatingStatus(true);
      
      try {
        console.log('🔄 handleStatusUpdate called with:', { 
          status, 
          selectedRequest: selectedRequest,
          requestId: selectedRequest.id 
        });
        
        // Check if this is a complaint or support request
        const isComplaint = selectedRequest.complainDate || selectedRequest.subject;
        
        if (isComplaint) {
          // Handle complaint status update
          const updatedComplaint = {
            ...selectedRequest,
            check: status === 'completed',
            adminNotes: adminNotes,
            updatedBy: auth.profile.email || auth.profile.uid,
            updatedAt: new Date().getTime()
          };
          
          console.log('🔄 Updating complaint status:', updatedComplaint);
          dispatch(editComplain(updatedComplaint, 'Edit'));
        } else {
          // Handle support request status update
          const updatedRequest = {
            ...selectedRequest,
            status: status,
            adminNotes: adminNotes,
            updatedBy: auth.profile.email || auth.profile.uid,
            updatedAt: new Date().getTime()
          };
          
          console.log('🔄 Updating support request status:', updatedRequest);
          dispatch(editSupportRequest(updatedRequest, 'UpdateStatus'));
        }
        
        // Show success message
        setCommonAlert({ 
          open: true, 
          msg: status === 'completed' 
            ? (t('request_completed_successfully') || 'Request completed successfully!')
            : (t('request_cancelled_successfully') || 'Request cancelled successfully!')
        });
        
        // Close modal immediately after successful update
        setViewModalOpen(false);
        setSelectedRequest(null);
        setEditingStatus('');
        setAdminNotes('');
        
      } catch (error) {
        console.error('❌ Error updating status:', error);
        setCommonAlert({ 
          open: true, 
          msg: t('error_updating_status') || 'Error updating status. Please try again.'
        });
      } finally {
        setUpdatingStatus(false);
      }
    }
  };

  // Helper function to count words
  const countWords = (text) => {
    return text.trim().split(/\s+/).filter(word => word.length > 0).length;
  };

  const handleAddCategory = async () => {
    // Basic validation
    if (!newCategory.name.trim() || !newCategory.description.trim()) {
      setCommonAlert({ 
        open: true, 
        msg: t('category_validation_failed') || 'Please fill in both category name and description.' 
      });
      return;
    }

    // Word count validation
    const nameWordCount = countWords(newCategory.name);
    const descriptionWordCount = countWords(newCategory.description);

    if (nameWordCount > 10) {
      setCommonAlert({ 
        open: true, 
        msg: t('category_name_max_words') || 'Category name must not exceed 10 words.' 
      });
      return;
    }

    if (descriptionWordCount > 10) {
      setCommonAlert({ 
        open: true, 
        msg: t('category_description_max_words') || 'Category description must not exceed 10 words.' 
      });
      return;
    }

    const category = {
      ...newCategory,
      active: true,
      createdAt: new Date().getTime()
    };
    
    try {
      // Call the action and wait for it to complete
      await dispatch(editSupportCategory(category, 'Add'));
      setNewCategory({ name: '', description: '' });
      setCommonAlert({ 
        open: true, 
        msg: t('category_added_successfully') || 'Category added successfully!' 
      });
    } catch (error) {
      console.error('Error adding category:', error);
      setCommonAlert({ 
        open: true, 
        msg: t('category_add_failed') || 'Failed to add category. Please try again.' 
      });
    }
  };

  const handleCommonAlertClose = (e) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    setCommonAlert({ open: false, msg: '' });
  };

  const handleCloseCategoriesModal = () => {
    setCategoriesModalOpen(false);
    setNewCategory({ name: '', description: '' }); // Reset form when closing
  };

  const handleDeleteCategory = (category) => {
    if (window.confirm(t('confirm_delete_category'))) {
      dispatch(editSupportCategory(category, 'Delete'));
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'completed': return 'success';
      case 'cancelled': return 'error';
      default: return 'default';
    }
  };

  const getUserTypeColor = (userType) => {
    switch (userType) {
      case 'customer': return 'primary';
      case 'driver': return 'secondary';
      default: return 'default';
    }
  };

  // Filtered data untuk card pertama (Complain)
  const filteredComplaints = React.useMemo(() => {
    const complaints = complainData?.list || [];
    return complaints.filter(complaint => {
      const matchesSearch = !searchText || 
        complaint.firstName?.toLowerCase().includes(searchText.toLowerCase()) ||
        complaint.lastName?.toLowerCase().includes(searchText.toLowerCase()) ||
        complaint.email?.toLowerCase().includes(searchText.toLowerCase()) ||
        complaint.subject?.toLowerCase().includes(searchText.toLowerCase()) ||
        complaint.body?.toLowerCase().includes(searchText.toLowerCase());
      
      return matchesSearch;
    });
  }, [complainData?.list, searchText]);

  // Filtered data untuk card kedua (Support Requests)  
  const filteredRequests = React.useMemo(() => {
    const requests = supportRequestData?.supportRequests || [];
    return requests.filter(request => {
      const matchesSearch = !searchText || 
        request.userName?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.userEmail?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.categoryTitle?.toLowerCase().includes(searchText.toLowerCase()) ||
        request.description?.toLowerCase().includes(searchText.toLowerCase());
      
      const matchesStatus = statusFilter === 'all' || request.status === statusFilter;
      
      return matchesSearch && matchesStatus;
    });
  }, [supportRequestData?.supportRequests, searchText, statusFilter]);

  const paginatedComplaints = filteredComplaints.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const paginatedRequests = filteredRequests.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const EmptyState = ({ cardType = 'default' }) => (
    <Box className={classes.emptyState}>
      <HeadsetMicIcon className={classes.emptyIcon} />
      <Typography className={classes.emptyTitle}>
        {cardType === 'complaints' 
          ? (t('no_complaints_yet') || 'No hay quejas y comentarios aún')
          : (t('no_support_requests_yet') || 'No Support Requests Yet')
        }
      </Typography>
      <Typography className={classes.emptyDescription}>
        {cardType === 'complaints'
          ? (t('complaints_empty_desc') || 'Cuando los usuarios envíen quejas y comentarios a través de la aplicación móvil, aparecerán aquí. Puede gestionar y responder a las consultas de los clientes desde este panel.')
          : (t('support_requests_empty_desc') || 'When customers submit support requests through the mobile app, they will appear here. You can manage and respond to customer inquiries from this dashboard.')
        }
      </Typography>
    </Box>
  );

  return (
    <PermissionGuard 
      hasAccess={settings?.support_module_enabled}
      featureName="Support Management"
    >
    <div className={classes.root}>
      <div className={classes.content}>
        
        {/* Card 1: Quejas y Comentarios */}
        <Card className={classes.tableCard} style={{ marginBottom: '24px' }}>
          <CardContent style={{ padding: 0 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" padding={2} marginBottom={0} style={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                {t('quejas_y_comentarios') || 'Quejas y Comentarios'}
              </Typography>
              <TextField
                label={t('search') || 'Buscar'}
                variant="outlined"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                size="small"
                style={{ width: '300px' }}
                placeholder="Buscar..."
              />
            </Box>
            
            <TableContainer style={{ minHeight: '100px' }}>
              <Table>
                <TableHead style={{ backgroundColor: colors.SECONDARY }}>
                  <TableRow>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('comportamiento') || 'Comportamiento'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('crear_fecha') || 'Crear fecha'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('nombre') || 'Nombre'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('apellido') || 'Apellido'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('tipo_de_usuario') || 'Tipo de usuario'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('correo_electronico') || 'Correo electrónico'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('numero_telefono_movil') || 'Número de teléfono móvil'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('mensaje') || 'Mensaje'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('asunto') || 'Asunto'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('fecha_del_proceso') || 'Fecha del proceso'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('estado') || 'Estado'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {complainData?.loading ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" style={{ height: '100px' }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredComplaints.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={11} align="center" style={{ height: '100px' }}>
                        <EmptyState cardType="complaints" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedComplaints.map((complaint) => (
                      <TableRow key={complaint.id} hover style={{ backgroundColor: editingRowId === complaint.id ? '#f5f5f5' : 'inherit' }}>
                        <TableCell align="center">
                          {editingRowId === complaint.id ? (
                            <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                              <Tooltip title="Save">
                                <IconButton
                                  color="success"
                                  onClick={() => handleEditComplaint(complaint)}
                                  size="small"
                                >
                                  <CheckIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Cancel">
                                <IconButton
                                  color="error"
                                  onClick={() => setEditingRowId(null)}
                                  size="small"
                                >
                                  <CloseIcon />
                                </IconButton>
                              </Tooltip>
                            </div>
                          ) : (
                              <Tooltip title="Edit">
                               <IconButton
                                 color="primary"
                                 onClick={() => handleEditComplaint(complaint)}
                                 size="small"
                               >
                                 <EditIcon />
                               </IconButton>
                             </Tooltip>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {moment(complaint.complainDate).format('DD/MM/YYYY HH:mm')}
                        </TableCell>
                        <TableCell align="center">
                          {complaint.firstName || 'Demo'}
                        </TableCell>
                        <TableCell align="center">
                          {complaint.lastName || 'User'}
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(complaint.role)}
                            color={getUserTypeColor(complaint.role)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">{complaint.email}</TableCell>
                        <TableCell align="center">{complaint.mobile}</TableCell>
                        <TableCell align="center">
                          <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {complaint.body}
                          </div>
                        </TableCell>
                        <TableCell align="center">{complaint.subject}</TableCell>
                        <TableCell align="center">
                          {moment(complaint.processDate).format('DD/MM/YYYY HH:mm')}
                        </TableCell>
                        <TableCell align="center">
                          {editingRowId === complaint.id ? (
                            <input
                              type="checkbox"
                              checked={editingData[complaint.id]?.check ?? complaint.check}
                              onChange={(e) => {
                                setEditingData({
                                  ...editingData,
                                  [complaint.id]: {
                                    ...editingData[complaint.id],
                                    check: e.target.checked
                                  }
                                });
                              }}
                              style={{ transform: 'scale(1.2)' }}
                            />
                          ) : (
                            complaint.check ? (
                              <CheckIcon style={{ color: '#4caf50', fontSize: '24px' }} />
                            ) : (
                              <PendingIcon style={{ color: '#ff9800', fontSize: '24px' }} />
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredComplaints.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Filas por página:"
              style={{ borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}
            />
          </CardContent>
        </Card>

        {/* Card 2: Support Request */}
        <Card className={classes.tableCard}>
          <CardContent style={{ padding: 0 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center" padding={2} marginBottom={0} style={{ borderBottom: '1px solid #e0e0e0' }}>
              <Typography variant="h6" style={{ fontWeight: 'bold', fontSize: '1.25rem' }}>
                {t('support_request') || 'Support Request'}
              </Typography>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SettingsIcon />}
                onClick={() => setCategoriesModalOpen(true)}
                className={classes.manageButton}
              >
                {t('manage_categories') || 'MANAGE CATEGORIES'}
              </Button>
            </Box>
            
            <TableContainer style={{ minHeight: '100px' }}>
              <Table>
                <TableHead style={{ backgroundColor: colors.SECONDARY }}>
                  <TableRow>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('name') || 'Name'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('category') || 'Category'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('estado') || 'Status'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('user_type') || 'User Type'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('date') || 'Date'}
                    </TableCell>
                    <TableCell style={{ color: colors.WHITE, fontWeight: 'bold', textAlign: 'center' }}>
                      {t('action') || 'Action'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supportRequestData?.loading ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" style={{ height: '100px' }}>
                        <CircularProgress />
                      </TableCell>
                    </TableRow>
                  ) : filteredRequests.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center" style={{ height: '100px' }}>
                        <EmptyState cardType="support" />
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedRequests.map((request) => (
                      <TableRow key={request.id} hover>
                        <TableCell align="center" style={{ fontWeight: 500 }}>
                          {request.userName}
                        </TableCell>
                        <TableCell align="center">{request.categoryTitle}</TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(request.status)}
                            color={getStatusColor(request.status)}
                            className={classes.statusChip}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            label={t(request.userType)}
                            color={getUserTypeColor(request.userType)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell align="center">
                          {moment(request.timestamp).format('MMM DD, YYYY HH:mm')}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={t('view_details') || 'Ver detalles'}>
                            <IconButton
                              color="primary"
                              onClick={() => handleViewRequest(request)}
                              className={classes.actionButton}
                              size="small"
                            >
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredRequests.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows per page:"
              style={{ borderTop: '1px solid #e0e0e0', backgroundColor: '#f8f9fa' }}
            />
          </CardContent>
        </Card>

              {/* View Request Details Modal */}
      <Dialog
        open={viewModalOpen}
        onClose={() => setViewModalOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'white',
          color: '#333',
          fontSize: '1.25rem',
          fontWeight: '600',
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center'
        }}>
          {t('support_request_details') || 'Support Request Details'}
          <IconButton 
            onClick={() => setViewModalOpen(false)}
            sx={{ 
              color: '#666',
              '&:hover': { backgroundColor: 'rgba(0,0,0,0.04)' }
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          {selectedRequest && (
            <Grid container spacing={3}>
              {/* User Information Section */}
              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: '600', 
                    color: '#666', 
                    fontSize: '0.875rem',
                    marginBottom: '4px'
                  }}>
                    {t('user_name') || 'User Name'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#333',
                    fontSize: '0.95rem'
                  }}>
                    {selectedRequest.userName || 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: '600', 
                    color: '#666', 
                    fontSize: '0.875rem',
                    marginBottom: '4px'
                  }}>
                    {t('phone') || 'Phone'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#333',
                    fontSize: '0.95rem'
                  }}>
                    {selectedRequest.phone || 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: '600', 
                    color: '#666', 
                    fontSize: '0.875rem',
                    marginBottom: '4px'
                  }}>
                    {t('email') || 'Email'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#1976d2',
                    fontSize: '0.95rem'
                  }}>
                    {selectedRequest.userEmail || 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} sm={6}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: '600', 
                    color: '#666', 
                    fontSize: '0.875rem',
                    marginBottom: '4px'
                  }}>
                    {t('user_type') || 'User Type'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#1976d2',
                    fontSize: '0.95rem'
                  }}>
                    {selectedRequest.userType || 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: '600', 
                    color: '#666', 
                    fontSize: '0.875rem',
                    marginBottom: '4px'
                  }}>
                    {t('support_category') || 'Support Category'}
                  </Typography>
                  <Typography variant="body1" sx={{ 
                    color: '#333',
                    fontSize: '0.95rem'
                  }}>
                    {selectedRequest.categoryTitle || selectedRequest.category || 'N/A'}
                  </Typography>
                </Box>
              </Grid>

              {/* Feedback Section */}
              <Grid item xs={12}>
                <Box>
                  <Typography variant="body2" sx={{ 
                    fontWeight: '600', 
                    color: '#666', 
                    fontSize: '0.875rem',
                    marginBottom: '8px'
                  }}>
                    {t('feedback') || 'Feedback'}
                  </Typography>
                  <Paper sx={{ 
                    padding: '16px', 
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '8px'
                  }}>
                    <Typography variant="body2" sx={{ 
                      color: '#333',
                      fontSize: '0.9rem',
                      lineHeight: 1.5
                    }}>
                      {selectedRequest.description || 'No description provided.'}
                    </Typography>
                  </Paper>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0',
          justifyContent: 'flex-end',
          gap: 2
        }}>
          <Button 
            onClick={() => handleStatusUpdate('cancelled')}
            disabled={updatingStatus}
            variant="outlined"
            sx={{
              color: '#d32f2f',
              borderColor: '#d32f2f',
              textTransform: 'none',
              fontWeight: '500',
              '&:hover': {
                borderColor: '#c62828',
                backgroundColor: 'rgba(211, 47, 47, 0.04)',
              },
              '&:disabled': {
                color: '#ccc',
                borderColor: '#ccc',
              }
            }}
          >
            {updatingStatus ? <CircularProgress size={20} /> : (t('cancel_the_request') || 'Cancel the request')}
          </Button>
          <Button
            onClick={() => handleStatusUpdate('completed')}
            disabled={updatingStatus}
            variant="contained"
            sx={{
              backgroundColor: '#4caf50',
              textTransform: 'none',
              fontWeight: '500',
              '&:hover': {
                backgroundColor: '#45a049',
              },
              '&:disabled': {
                backgroundColor: '#ccc',
              }
            }}
          >
            {updatingStatus ? <CircularProgress size={20} color="inherit" /> : (t('complete_the_request') || 'Complete the request')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Categories Modal */}
      <Dialog
        open={categoriesModalOpen}
        onClose={handleCloseCategoriesModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
          }
        }}
      >
        <DialogTitle sx={{ 
          backgroundColor: 'white',
          color: '#333',
          fontSize: '1.25rem',
          fontWeight: '600',
          padding: '20px 24px',
          borderBottom: '1px solid #e0e0e0',
          marginBottom: 0,
        }}>
          {t('manage_support_categories') || 'Manage Support Categories'}
        </DialogTitle>
        <DialogContent sx={{ padding: '24px' }}>
          {/* Add New Category Section */}
          <Box sx={{ marginBottom: '32px' }}>
            <Typography variant="h6" gutterBottom sx={{ 
              fontWeight: '600', 
              color: '#333',
              marginBottom: '16px',
              fontSize: '1.1rem'
            }}>
              {t('add_new_category') || 'Add New Category'}
            </Typography>
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('category_name') || 'Category Name'}
                  placeholder={t('enter_category_name') || 'Enter category name'}
                  value={newCategory.name}
                  onChange={(e) => setNewCategory({ ...newCategory, name: e.target.value })}
                  variant="outlined"
                  size="small"
                  helperText={`${countWords(newCategory.name)}/10 ${t('words') || 'words'}`}
                  error={countWords(newCategory.name) > 10}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label={t('description') || 'Description'}
                  placeholder={t('enter_category_description') || 'Enter category description'}
                  value={newCategory.description}
                  onChange={(e) => setNewCategory({ ...newCategory, description: e.target.value })}
                  multiline
                  rows={3}
                  variant="outlined"
                  helperText={`${countWords(newCategory.description)}/10 ${t('words') || 'words'}`}
                  error={countWords(newCategory.description) > 10}
                />
              </Grid>
              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end" sx={{ mt: 1 }}>
                  <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={handleAddCategory}
                    disabled={!newCategory.name.trim() || !newCategory.description.trim()}
                    sx={{
                      backgroundColor: '#1976d2',
                      textTransform: 'none',
                      fontWeight: '500',
                      padding: '8px 20px',
                      '&:hover': {
                        backgroundColor: '#1565c0',
                      },
                      '&:disabled': {
                        backgroundColor: '#ccc',
                      }
                    }}
                  >
                    {t('add_category') || 'Add Category'}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Existing Categories Section */}
          <Typography variant="h6" gutterBottom sx={{ 
            fontWeight: '600', 
            color: '#333',
            marginBottom: '16px',
            fontSize: '1.1rem'
          }}>
            {t('existing_categories') || 'Existing Categories'}
          </Typography>
          
          {supportCategoryData.supportCategories?.length > 0 ? (
            <TableContainer component={Paper} sx={{ 
              border: '1px solid #e0e0e0',
              boxShadow: 'none'
            }}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ 
                      fontWeight: '600', 
                      fontSize: '0.875rem',
                      color: '#666',
                      backgroundColor: '#fafafa'
                    }}>
                      {t('name') || 'Name'}
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: '600', 
                      fontSize: '0.875rem',
                      color: '#666',
                      backgroundColor: '#fafafa'
                    }}>
                      {t('description') || 'Description'}
                    </TableCell>
                    <TableCell sx={{ 
                      fontWeight: '600', 
                      fontSize: '0.875rem',
                      color: '#666',
                      backgroundColor: '#fafafa',
                      textAlign: 'center',
                      width: '100px'
                    }}>
                      {t('action') || 'Action'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {supportCategoryData.supportCategories.map((category) => (
                    <TableRow 
                      key={category.id}
                      sx={{
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        }
                      }}
                    >
                      <TableCell sx={{ 
                        fontWeight: '500',
                        color: '#333',
                        fontSize: '0.875rem'
                      }}>
                        {category.name}
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#666',
                        fontSize: '0.875rem',
                        maxWidth: '300px',
                        wordWrap: 'break-word'
                      }}>
                        {category.description}
                      </TableCell>
                      <TableCell sx={{ textAlign: 'center' }}>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteCategory(category)}
                          size="small"
                          sx={{
                            '&:hover': {
                              backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            }
                          }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ 
              padding: '40px',
              textAlign: 'center',
              backgroundColor: '#fafafa',
              border: '1px solid #e0e0e0'
            }}>
              <Typography variant="body1" sx={{ 
                color: '#666',
                marginBottom: '8px'
              }}>
                {t('no_categories_found') || 'No categories found'}
              </Typography>
              <Typography variant="body2" sx={{ 
                color: '#999'
              }}>
                {t('add_first_category') || 'Add your first support category using the form above'}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ 
          padding: '16px 24px',
          borderTop: '1px solid #e0e0e0'
        }}>
          <Button 
            onClick={handleCloseCategoriesModal}
            sx={{
              textTransform: 'none',
              color: '#666'
            }}
          >
            {t('close') || 'Close'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Alert Dialog */}
      <AlertDialog open={commonAlert.open} onClose={handleCommonAlertClose}>
        {commonAlert.msg}
      </AlertDialog>
      </div>
    </div>
    </PermissionGuard>
  );
} 